/**
 * Boundary checks for a view's stored settings payload. A payload is the
 * view's own shape, but it arrives as unknown jsonb (an older version, a
 * list's shared view, a hand edit), so each view keeps only the parts that
 * check out and applies its defaults for the rest.
 *
 * Pure: no Vue/Apollo imports.
 */
import type { Field } from "@auger/core/fields"
import type { FieldKey, GroupBy, SortBy } from "../useCollection"

export const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const isDirection = (value: unknown): value is SortBy["direction"] =>
  value === "asc" || value === "desc"

/** A list of strings, or undefined when the value is anything else. */
export function stringList(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((v): v is string => typeof v === "string")
    ? value
    : undefined
}

/** A record of numbers (e.g. column widths), or undefined when any value isn't one. */
export function numberRecord(value: unknown): Record<string, number> | undefined {
  if (!isObject(value)) return undefined
  const out: Record<string, number> = {}
  for (const [key, entry] of Object.entries(value)) {
    if (typeof entry !== "number") return undefined
    out[key] = entry
  }
  return out
}

const isSortBy = (k: unknown): k is SortBy =>
  isObject(k) && typeof k.key === "string" && isDirection(k.direction)

const isGroupBy = (k: unknown): k is GroupBy =>
  isObject(k) &&
  typeof k.key === "string" &&
  (k.direction === undefined || isDirection(k.direction))

/** The sorting, when every entry is a { key, direction }. */
export function sortByList(value: unknown): SortBy[] | undefined {
  return Array.isArray(value) && value.every(isSortBy) ? value : undefined
}

/** The grouping, when every entry is a { key } with an optional direction. */
export function groupByList(value: unknown): GroupBy[] | undefined {
  return Array.isArray(value) && value.every(isGroupBy) ? value : undefined
}

/** The fields named by `keys`, in that order; a key naming no field drops out. */
export function fieldsByKey(keys: readonly FieldKey[], fields: readonly Field[]): Field[] {
  return keys.flatMap((key) => fields.find((f) => f.key === key) ?? [])
}
